import { Stack, Text } from '@ltvco/refresh-lib/theme';
import { EventBusy, Person, Restore } from '@mui/icons-material';

import {
  RecallDetailImage,
  RecallDetailImageWrapper,
  RecallReportMetricLabelGroup,
  RecallReportMetricRow,
  RecallReportMetricValue,
  ResultsContainer,
} from './components/index';
import { getDetailConfig } from './utils';

import type { RecallItem } from '../interfaces';
import type { FC } from 'react';

interface RecallReportResultsProps {
  item: RecallItem | null;
}

export const RecallReportResults: FC<RecallReportResultsProps> = ({ item }) => {
  if (!item) {
    return null;
  }

  const { recallDate, status, severity, subtitle, title, fields } =
    getDetailConfig(item);

  return (
    <ResultsContainer>
      <Stack direction="row" alignItems="center" gap={0.5} sx={{ mb: 2.5 }}>
        <Restore sx={{ color: 'text.secondary', fontSize: 18 }} />
        <Text
          variant="caption"
          sx={{
            textTransform: 'uppercase',
            color: 'text.secondary',
            fontWeight: 400,
          }}
        >
          Recall Date: {recallDate}
        </Text>
      </Stack>

      {subtitle && (
        <Text variant="h4" fontWeight={400} sx={{ fontSize: '20px', mb: 1.5 }}>
          {subtitle}
        </Text>
      )}

      {title && (
        <Text
          fontWeight={700}
          sx={{ fontSize: '30px', lineHeight: 1.2, mb: 4 }}
        >
          {title}
        </Text>
      )}

      {(status || severity) && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems="stretch"
          gap={{ xs: 2, sm: 4 }}
          sx={{ mb: 4 }}
        >
          {status ? (
            <RecallReportMetricRow>
              <RecallReportMetricLabelGroup>
                <Person sx={{ fontSize: 18, color: 'primary.main' }} />
                <Text fontWeight={700} color="text.secondary">
                  Status
                </Text>
              </RecallReportMetricLabelGroup>
              <RecallReportMetricValue>{status}</RecallReportMetricValue>
            </RecallReportMetricRow>
          ) : null}
          {severity ? (
            <RecallReportMetricRow>
              <RecallReportMetricLabelGroup>
                <EventBusy sx={{ fontSize: 18, color: 'primary.main' }} />
                <Text fontWeight={700} color="text.secondary">
                  Severity
                </Text>
              </RecallReportMetricLabelGroup>
              <RecallReportMetricValue>{severity}</RecallReportMetricValue>
            </RecallReportMetricRow>
          ) : null}
        </Stack>
      )}

      <Stack spacing={2}>
        {fields
          .filter(({ value }) => value)
          .map(({ label, value }) => (
            <Stack key={label}>
              <Text variant="body1" fontWeight={700} sx={{ mb: 0.5 }}>
                {label}
              </Text>
              {label === 'Images' && value ? (
                <Stack direction="row" flexWrap="wrap" gap={2} sx={{ mt: 0.5 }}>
                  {value.split(',').map((url) => {
                    const src = url.trim();
                    if (!src) {
                      return null;
                    }
                    return (
                      <RecallDetailImageWrapper key={src}>
                        <a
                          href={src}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ display: 'block' }}
                        >
                          <RecallDetailImage
                            src={src}
                            alt="Recall product"
                            loading="lazy"
                          />
                        </a>
                      </RecallDetailImageWrapper>
                    );
                  })}
                </Stack>
              ) : (
                <Text variant="body1">{value}</Text>
              )}
            </Stack>
          ))}
      </Stack>
    </ResultsContainer>
  );
};
